import React from 'react';
import { motion } from 'framer-motion';
import { TrophyIcon } from 'lucide-react';
import { ChatPanel } from './ChatPanel';
import { Confetti } from './Confetti';
import { CountdownStage } from './CountdownStage';
import { useCountdown } from '../hooks/useCountdown';
import { browserTimeZone } from '../utils/schedule';
import type { Session } from '../types/session';
import type { useRoom } from '../hooks/useRoom';
interface ParticipantViewProps {
  session: Session;
  roomApi: ReturnType<typeof useRoom>;
}
const time = (ms: number | null) => ms ? new Date(ms).toLocaleTimeString([], {
  hour: 'numeric',
  minute: '2-digit'
}) : '';
export function ParticipantView({
  session,
  roomApi
}: ParticipantViewProps) {
  const {
    room,
    participants,
    winners
  } = roomApi;
  const closed = room.closed;
  const winner = room.lastWinner;
  const mine = session.name.trim().toLowerCase();
  const youWon = winner?.name.trim().toLowerCase() === mine;
  const remaining = useCountdown(room.startsAt);
  const pending = !closed && room.startsAt !== null && remaining.total > 0;

  // Before the scheduled start there is no room to talk in yet — the countdown
  // takes the whole screen and chat only appears once the event goes live.
  if (pending && room.startsAt !== null) {
    return <main className="flex flex-1" data-id="element-272" data-mp="2mgk12hdftp37">
        <CountdownStage eventName={room.name} startsAt={room.startsAt} timeZone={room.timeZone ?? browserTimeZone()} footnote={`You are in as ${session.name} · ${participants.filter(p => !p.blocked).length} people waiting · chat opens when the event starts`} data-id="element-273" data-mp="2rchiys4cn3i3" />
      </main>;
  }
  return <main className="grid flex-1 grid-cols-1 items-stretch lg:grid-cols-[1fr_22rem] xl:grid-cols-[1fr_25rem]" data-id="element-274" data-mp="28msspcbg0b92">
      <div className="relative flex min-w-0 flex-col items-center px-5 py-12 text-center sm:px-10" data-id="element-275" data-mp="1tpsdg8afu8rh">
        {winner && !closed && <Confetti key={winner.at} data-id="element-276" data-mp="1ynk1usbvjcqx" />}

        <p className={`text-[0.7rem] font-semibold uppercase tracking-[0.28em] ${closed ? 'text-muted' : 'text-gold'}`} data-id="element-277" data-mp="1yntwwubs0a3h">
          {closed ? 'Event closed' : winner ? youWon ? 'You won' : 'Latest winner' : 'Draw in progress'}
        </p>

        {closed ? <>
            <h2 className="mt-6 font-display text-[clamp(2.25rem,7vw,4.5rem)] leading-tight text-white" data-id="element-278" data-mp="10szl3zclc9zt">
              This event has ended
            </h2>
            <p className="mt-4 max-w-md text-sm text-muted" data-id="element-279" data-mp="1tkt0xpoqmtgh">
              The host closed {room.name}. You can still read the chat and the full
              winners list below.
            </p>
          </> : winner ? <motion.h2 key={winner.at} initial={{
        opacity: 0,
        scale: 0.96,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-6 break-words font-display text-[clamp(2.75rem,9vw,6.5rem)] leading-[1.02] text-white" data-id="element-280" data-mp="1jlbx7dt1qpn5">
            {winner.name}
          </motion.h2> : <h2 className="mt-6 font-display text-[clamp(2rem,6vw,3.5rem)] leading-tight text-white" data-id="element-281" data-mp="2oqs6i3mwvsi8">
            Waiting for the host to spin
          </h2>}

        {!closed && <p className="mt-5 text-sm text-muted" data-id="element-282" data-mp="2ykr913qwdeap">
            You are in as <span className="text-white" data-id="element-283" data-mp="33gptig93uc5u">{session.name}</span> ·{' '}
            {participants.filter(p => !p.blocked).length} people in the room
          </p>}

        <section aria-label="Winners so far" className="mt-12 w-full max-w-xl text-left" data-id="element-284" data-mp="3cipche3ojqbf">
          <div className="flex items-baseline justify-between gap-3 border-b border-line pb-2" data-id="element-285" data-mp="3hhsd8mcmnadl">
            <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-286" data-mp="1i57wqd9inxqy">
              Winners so far
            </h3>
            <span className="text-xs tabular-nums text-muted" data-id="element-287" data-mp="1n3rgq84juzgo">{winners.length}</span>
          </div>

          {winners.length === 0 ? <p className="py-6 text-sm text-muted" data-id="element-288" data-mp="38ls7jfqinu3l">
              No one has been drawn yet — winners appear here the moment they are picked.
            </p> : <ol className="max-h-[34vh] divide-y divide-line/70 overflow-y-auto pr-1" data-id="element-289" data-mp="3mj6zar48sdyq">
              {winners.map((w, i) => {
            const isMe = w.name.trim().toLowerCase() === mine;
            return <li key={w.id} className="flex items-center gap-3 py-2.5" data-id="element-290" data-mp="2pvaibl4w7gmz" data-mpi={i}>
                    <span className="w-6 shrink-0 text-right text-xs tabular-nums text-muted" data-id="element-291" data-mp="2pt7ik9dxu952">
                      {winners.length - i}
                    </span>
                    <span className={`min-w-0 flex-1 truncate text-sm ${isMe ? 'font-semibold text-gold' : 'text-body'}`} data-id="element-292" data-mp="2yuxbjjngxg4y">
                      {w.name}
                      {isMe && <span className="ml-2 text-xs text-muted" data-id="element-293" data-mp="1x0vxwlmtqoka">you</span>}
                    </span>
                    {i === 0 && <TrophyIcon className="h-3.5 w-3.5 shrink-0 text-gold" aria-label="Most recent" data-id="element-294" data-mp="21s47pis2gbxz" />}
                    <span className="shrink-0 text-[11px] tabular-nums text-muted" data-id="element-295" data-mp="1s2kmi6gvs5zy">
                      {time(w.at)}
                    </span>
                  </li>;
          })}
            </ol>}
        </section>
      </div>

      <div className="flex flex-col overflow-hidden border-t border-line px-5 py-5 sm:px-7 lg:h-[calc(100vh-7.5rem)] lg:border-l lg:border-t-0" data-id="element-296" data-mp="25sxto6ly7go8">
        <h2 className="pb-3 text-lg font-semibold text-white" data-id="element-297" data-mp="2aqpmd2uf36th">Room chat</h2>
        <ChatPanel session={session} room={roomApi.room} messages={roomApi.messages} participants={roomApi.participants} me={roomApi.me} onSend={roomApi.sendMessage} onDeleteMessage={roomApi.deleteMessage} onWipeChat={roomApi.wipeChat} onSetChatEnabled={roomApi.setChatEnabled} onSetMuted={roomApi.setMuted} onSetBlocked={roomApi.setBlocked} data-id="element-298" data-mp="ud94r9p4w7ks" />
      </div>
    </main>;
}